import { useMutation } from 'react-query';
import moment from 'moment';
import { shallow } from 'zustand/shallow';
import { getPlanById } from '../../../../api/services/mediaPlanning/getPlanById';
import usePlanStore from '../../../../store/planStore';
import { useCountriesDataStore } from '../../../../store/countriesDataStore';
import { Countries } from '../../../../shared/enums';

const useSingleMediaPlan = (
  getMetaPredictions?: () => void,
  getMetaPredictionStatuses?: () => void
) => {
  const {
    setGeneralInfoState,
    updateCountryForm,
    saveCountryFormState,
    saveGeneralInfoState,
    setIsPlanCreating,
    setSelectedCountry,
    updateSavedCalculatedBudget,
    setMetaBooked,
    savedGeneralInfoState,
    storeDigitalCountryFormState,
    storeOfflineCountryFormState,
    updateMetaPredictionsState,
    updateTVAvailable,
    setIsLocked,
  } = usePlanStore();

  const countriesDataStore = useCountriesDataStore((state) => state, shallow);

  const { mutate: fetchMediaPlan, isLoading } = useMutation({
    mutationFn: (id: string) => getPlanById(id),
    onSuccess: async ({ data }) => {
      if (savedGeneralInfoState) return;

      setIsLocked(!!data.isLocked);

      setGeneralInfoState({
        campaignName: data.campaignName,
        campaignCode: data.campaignCode,
        division: data.division,
        brand: data.brand,
        category: data.category,
      });

      Object.entries(data.countrySetup).forEach(([key, value]) => {
        // TODO: Remove it from here
        setMetaBooked(true);
        updateCountryForm(key, value);

        const formattedStartDate = moment(value.startDate).format('DD/MM/YYYY');
        updateCountryForm(key, {
          isCountrySetupConfirmed: true,
          startDate: formattedStartDate,
          availableTouchpoints: value.availableTouchpoints,
          metaConfig: {
            ...value.metaConfig,
            startDate: moment(value.metaConfig?.startDate, 'YYYY-MM-DD').format(
              'DD/MM/YYYY'
            ),
          },
          offline: {
            ...value.offline,
            tv: value.offline?.tv
              ? {
                  ...value.offline.tv,
                  ...(value.offline.tv.startDate && {
                    startDate: moment(
                      value.offline.tv.startDate,
                      'YYYY-MM-DD'
                    ).format('DD-MM-YYYY'),
                  }),
                  ...(value.offline.tv.endDate && {
                    endDate: moment(
                      value.offline.tv.endDate,
                      'YYYY-MM-DD'
                    ).format('DD-MM-YYYY'),
                  }),
                }
              : undefined,
          },
        });

        updateTVAvailable(
          key,
          !!(value.isTVMandatory || value.isTVIncluded === true)
        );

        saveCountryFormState(key);
        storeDigitalCountryFormState(key);
        storeOfflineCountryFormState(key);

        updateMetaPredictionsState(
          key,
          value.metaConfig?.metaCampaignId,
          value.metaConfig?.metaPredictionId
        );

        const includedOptionalTouchpoints = value.calculatedBudget?.filter(
          (budget) => !budget.isMandatory && budget.isIncludedInCalculations
        );

        const oohIncludedOptional =
          value.offline?.ooh?.calculatedBudget?.data?.filter(
            (row) => !row.isRequired && row.isIncludedInCalculations
          );

        const countryEnumKey = Countries[key as keyof typeof Countries];

        const prevOptionalIds =
          countriesDataStore[countryEnumKey]?.optionalIds ?? {};

        const newOptionalIds = { ...prevOptionalIds };

        includedOptionalTouchpoints?.forEach((tp) => {
          const identifier = `${tp.touchpointName}${tp.touchpointFunnel}`;
          newOptionalIds[identifier] = true;
        });

        oohIncludedOptional?.forEach((row) => {
          const identifier = `${row.format}${row.variant}`;
          newOptionalIds[identifier] = true;
        });

        countriesDataStore[countryEnumKey]?.updateOptionalIds(newOptionalIds);

        updateCountryForm(key, {
          metaConfig: {
            ...value.metaConfig,
            startDate: moment(value.metaConfig?.startDate, 'YYYY-MM-DD').format(
              'DD/MM/YYYY'
            ),
            metaCampaignId: undefined,
            metaPredictionId: undefined,
          },
        });

        updateSavedCalculatedBudget(key, value.calculatedBudget ?? []);
      });

      setSelectedCountry(Object.entries(data.countrySetup)[0][0]);
      saveGeneralInfoState();
      setIsPlanCreating(true);

      const isAnyMetaNotReserved = Object.values(data.countrySetup).some(
        (country) => {
          const status = country.metaConfig?.reservationStatus;
          return status !== 'reserved' && status !== 'partially-reserved';
        }
      );

      if (getMetaPredictions && isAnyMetaNotReserved && !data.isLocked) {
        await getMetaPredictions();
      }

      if (getMetaPredictionStatuses && isAnyMetaNotReserved && !data.isLocked) {
        getMetaPredictionStatuses();
      }
    },
  });

  return { fetchMediaPlan, isLoading };
};

export default useSingleMediaPlan;
